import React, { memo, useEffect, useMemo, useRef, useState } from 'react';
import { ApplicationIcon, Typography } from '@saviynt/design-system';
import { select } from 'd3-selection';

import { butterflyData, type NodeData } from './Butterfly1Data';

import './ButterflyGraph1.css';

interface NodePosition {
  x: number;
  y: number;
  data: NodeData;
  level: number;
}

type ClusterType = 'application' | 'identity' | 'vault';

// Memoized node component for better performance
const Node = memo(
  ({
    x,
    y,
    nodeData,
    level,
    onNodeClick,
  }: {
    x: number;
    y: number;
    nodeData: NodeData;
    level: number;
    onNodeClick: (id: string) => void;
  }) => {
    const isRoot = level === 0;
    const size = isRoot ? 66 : 55;
    const iconSize = isRoot ? 'xxLarge' : 'small';

    return (
      <>
        {/* Icon node */}
        <div
          role='button'
          tabIndex={0}
          style={{
            position: 'absolute',
            left: x - size / 2,
            top: y - size / 2,
            width: size,
            height: size,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'auto',
            borderRadius: '50%',
          }}
          onClick={() => onNodeClick(nodeData.id ?? '')}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onNodeClick(nodeData.id ?? '');
            }
          }}>
          <ApplicationIcon logo={nodeData.appName} size={iconSize} />
        </div>

        {/* Labels for root node */}
        {isRoot && <RootLabels x={x} y={y} size={size} />}

        {/* Label below node */}
        {!isRoot && (
          <div
            style={{
              position: 'absolute',
              left: x,
              top: y + size / 2,
              transform: 'translateX(-50%)',
              whiteSpace: 'nowrap',
              textAlign: 'center',
              pointerEvents: 'none',
            }}>
            <Typography kind='body3' color='--color-background-neutral-bold'>
              {nodeData.appName}
            </Typography>
          </div>
        )}
      </>
    );
  }
);

// Extract root labels to a separate component
const RootLabels = memo(
  ({ x, y, size }: { x: number; y: number; size: number }) => {
    // Common label style
    const labelStyle = {
      position: 'absolute' as const,
      transform: 'translateX(-50%)',
      whiteSpace: 'nowrap' as const,
      textAlign: 'center' as const,
      pointerEvents: 'none' as const,
    };

    const labels = [
      { left: x, top: y + size / 2 + 40, text: 'Discovery Source', kind: 'h2' },
      { left: x, top: y - 400, text: 'Get Started', kind: 'display4-bold' },
      { left: x + 500, top: y, text: 'Applications', kind: 'h2' },
      { left: x - 550, top: y - 250, text: 'Identity Sources', kind: 'h2' },
      { left: x - 550, top: y + 180, text: 'Vaults', kind: 'h2' },
    ];

    return (
      <>
        {labels.map((label) => (
          <div
            key={`label-${label.text}`}
            style={{
              ...labelStyle,
              left: label.left,
              top: label.top,
            }}>
            <Typography kind={label.kind}>{label.text}</Typography>
          </div>
        ))}
      </>
    );
  }
);

/**
 * Butterfly Graph
 */
function ButterflyGraph1() {
  const width = 1500;
  const height = 1000;
  const svgRef = useRef<SVGSVGElement | null>(null);
  const [nodePositions, setNodePositions] = useState<NodePosition[]>([]);

  // Memoize center coordinates
  const center = useMemo(
    () => ({ x: width / 2, y: height / 2 }),
    [width, height]
  );

  useEffect(() => {
    const svg = select(svgRef.current);

    svg.selectAll('*').remove();
    svg.attr('width', width).attr('height', height);
    const g = svg.append('g');

    // Filter data once
    const applications = butterflyData.filter(
      (d) => d.category === 'applications'
    );
    const identities = butterflyData.filter((d) => d.category === 'identity');
    const vaults = butterflyData.filter((d) => d.category === 'vaults');

    const nodeList: NodePosition[] = [];
    const root: NodeData = { id: 'ServiceNow', appName: 'ServiceNow' };

    nodeList.push({ x: center.x, y: center.y, data: root, level: 0 });

    // Unified draw function that handles all cluster types
    const drawLinearCluster = (
      items: NodeData[],
      type: ClusterType,
      baseX: number,
      baseY: number,
      dx: number,
      dy: number
    ) => {
      items.forEach((node, i) => {
        let x: number;
        let y: number;

        if (type === 'vault') {
          x = baseX - dx * i;
          y = baseY + dy * i;
        } else if (type === 'identity') {
          x = baseX + dx * i;
          y = baseY + dy * i;
        } else {
          // application
          const midNode = items.length / 2;

          if (i <= midNode) {
            x = baseX - dx * i;
            y = baseY + dy * i;
          } else {
            // Calculate position for nodes after midpoint
            const lastNodeX = baseX - dx * Math.floor(midNode);

            x = lastNodeX + dx * (i - Math.floor(midNode));
            y = baseY + dy * i;
          }
        }

        nodeList.push({ x, y, data: node, level: 1 });

        // Draw connecting line
        g.append('line')
          .attr('x1', center.x)
          .attr('y1', center.y)
          .attr('x2', x)
          .attr('y2', y)
          .attr('stroke', '#969696')
          .attr('stroke-width', 0.5);
      });
    };

    const spacing = 70;
    const centerGap = 250;

    // Applications cluster
    const totalAppHeight = (applications.length - 1) * spacing;
    const rightStartY = center.y - totalAppHeight / 2;

    drawLinearCluster(
      applications,
      'application',
      center.x + 400,
      rightStartY,
      20,
      spacing
    );

    // Identities cluster
    const totalIdentityHeight = (identities.length - 1) * spacing;
    const identityEndY = center.y - centerGap / 2;
    const identityStartY = identityEndY - totalIdentityHeight;

    drawLinearCluster(
      identities,
      'identity',
      center.x - 400,
      identityStartY,
      10,
      spacing
    );

    // Vaults cluster
    const vaultStartY = center.y + centerGap / 2;

    drawLinearCluster(
      vaults,
      'vault',
      center.x - 350,
      vaultStartY,
      10,
      spacing
    );

    setNodePositions(nodeList);
  }, [center]);

  const onNodeClick = () => {
    // Handle node click
  };

  return (
    <div
      className='graph-container'
      style={{ position: 'relative', width, height }}>
      <svg
        ref={svgRef}
        className='graph-svg'
        style={{ position: 'absolute', top: 0, left: 0 }}
      />
      {nodePositions.map(({ x, y, data: nodeData, level }, i) => (
        <Node
          key={nodeData.id ?? `node-${i}`}
          x={x}
          y={y}
          nodeData={nodeData}
          level={level}
          onNodeClick={onNodeClick}
        />
      ))}
    </div>
  );
}

export default ButterflyGraph1;
